import { useState } from "react";
import { Navbar } from "../components/Navbar";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, Send, Loader2, Shield } from "lucide-react";

export default function ReportAbusePage() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    type: "",
    url: "",
    description: "",
    email: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate sending
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success("Segnalazione inviata. Esamineremo il caso entro 48 ore.");
    setFormData({ type: "", url: "", description: "", email: "" });
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm border p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Segnala un abuso</h1>
            <p className="text-slate-500 mt-2">
              Aiutaci a mantenere la piattaforma sicura e affidabile
            </p>
          </div>

          {/* Info box */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8">
            <div className="flex gap-3">
              <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold mb-1">La tua segnalazione è importante</p>
                <p>
                  Esaminiamo tutte le segnalazioni entro 48 ore lavorative. 
                  I contenuti che violano i nostri Termini e Condizioni verranno rimossi.
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label>Tipo di violazione *</Label>
              <Select
                value={formData.type}
                onValueChange={(v) => setFormData(prev => ({ ...prev, type: v }))}
                required
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona il tipo di problema" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fake_job">Annuncio di lavoro falso</SelectItem>
                  <SelectItem value="scam">Truffa / Phishing</SelectItem>
                  <SelectItem value="discrimination">Contenuto discriminatorio</SelectItem>
                  <SelectItem value="harassment">Molestie / Comportamento inappropriato</SelectItem>
                  <SelectItem value="illegal">Attività illegale</SelectItem>
                  <SelectItem value="spam">Spam / Pubblicità non autorizzata</SelectItem>
                  <SelectItem value="fake_profile">Profilo falso / Identità rubata</SelectItem>
                  <SelectItem value="other">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>URL o riferimento del contenuto</Label>
              <Input
                value={formData.url}
                onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                placeholder="Es: link all'annuncio o nome utente"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Descrizione dettagliata *</Label>
              <Textarea
                required
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descrivi il problema nel modo più dettagliato possibile..."
                rows={5}
                className="mt-1"
              />
            </div>

            <div>
              <Label>La tua email (opzionale)</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Per ricevere aggiornamenti sulla segnalazione"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Non condivideremo la tua email con terzi
              </p>
            </div>

            <Button type="submit" disabled={loading} className="w-full bg-red-600 hover:bg-red-700">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Invia segnalazione
            </Button>
          </form>

          {/* Contact alternative */}
          <div className="mt-8 pt-6 border-t text-center">
            <p className="text-sm text-slate-500 mb-2">
              Per emergenze o casi gravi, contattaci direttamente:
            </p>
            <a 
              href="mailto:info@lavoroticino.ch" 
              className="text-[#004E64] font-medium hover:underline"
            >
              info@lavoroticino.ch
            </a>
          </div>
        </div>

        {/* Back link */}
        <div className="text-center mt-6">
          <Link to="/" className="text-slate-500 hover:text-slate-700 text-sm">
            ← Torna alla home
          </Link>
        </div>
      </div>
    </div>
  );
}
